import React, { useMemo, useState, useRef } from "react";
import { DashboardFleetSummaryCard } from "../../Dashboard/Cards/summary";
import { Service } from "../../../../redux/services/serviceApi";

interface ServicesSummaryProps {
  services: Service[];
  isLoading: boolean;
  onFilterService?: (serviceName: string) => void;
}

const ServicesSummary: React.FC<ServicesSummaryProps> = ({
  services,
  isLoading,
  onFilterService,
}) => {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [clickedCard, setClickedCard] = useState<string | null>(null);
  const clickTimeoutRef = useRef<number | null>(null);

  const metrics = useMemo(() => {
    if (!services || services.length === 0) {
      return {
        totalActiveServices: 0,
        mostRequestedService: { name: "N/A", count: 0 },
        lessRequestedService: { name: "N/A", count: 0 },
        totalRevenue: 0,
        monthlyGrowth: 0,
        revenueGrowth: 0,
      };
    }

    const totalActiveServices = services.length;

    const serviceFrequency: Record<string, number> = {};
    services.forEach((service) => {
      const serviceName = service.service_type_name;
      serviceFrequency[serviceName] = (serviceFrequency[serviceName] || 0) + 1;
    });

    // const mostRequested = Object.entries(serviceFrequency).reduce(
    //   (max, [name, count]) => (count > max.count ? { name, count } : max),
    //   { name: "N/A", count: 0 }
    // );

    // const lessRequested = Object.entries(serviceFrequency).reduce(
    //   (min, [name, count]) =>
    //     count < min.count || min.count === 0 ? { name, count } : min,
    //   { name: "N/A", count: Infinity }
    // );

    const totalRevenue = services.reduce((sum, service) => {
      const price = parseFloat(service.price || "0");
      return sum + (isNaN(price) ? 0 : price);
    }, 0);

    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

    const lastMonthServices = services.filter((s) => {
      const d = new Date(s.created_at);
      return d >= thirtyDaysAgo && d <= now;
    }).length;

    const previousMonthServices = services.filter((s) => {
      const d = new Date(s.created_at);
      return d >= sixtyDaysAgo && d < thirtyDaysAgo;
    }).length;

    const monthlyGrowth =
      previousMonthServices > 0
        ? ((lastMonthServices - previousMonthServices) / previousMonthServices) * 100
        : lastMonthServices > 0 ? 100 : 0;

    return {
      totalActiveServices,
      // mostRequestedService: mostRequested,
      // lessRequestedService:
      //   lessRequested.count === Infinity ? { name: "N/A", count: 0 } : lessRequested,
      totalRevenue,
      monthlyGrowth: Math.round(monthlyGrowth * 10) / 10,
      revenueGrowth: 0,
    };
  }, [services]);

  const handleCardClick = (cardKey: string, serviceName?: string) => {
    if (clickTimeoutRef.current) window.clearTimeout(clickTimeoutRef.current);
    setClickedCard(cardKey);
    clickTimeoutRef.current = window.setTimeout(() => {
      setClickedCard(null);
      clickTimeoutRef.current = null;
    }, 3000);
    if (typeof onFilterService === "function") {
      onFilterService(serviceName || "");
    }
  };

  const getCardStyle = (key: string) =>
    clickedCard === key
      ? { cursor: "pointer", boxShadow: "0 8px 24px rgba(20,56,135,0.34)" }
      : { cursor: "pointer" };

  if (isLoading) {
    return (
      <section className="bg-[#E7E9EE] p-4 rounded-lg mt-10">
        <h3 className="text-sm font-semibold text-[#00032A] mb-4">Summary</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-[12px] shadow-sm p-4 h-[150px] animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#E7E9EE] p-4 rounded-lg mt-10">
      <h3 className="text-sm font-semibold text-[#00032A] mb-4">Summary</h3>
      <div className="grid grid-cols-1 gap-6">

        {/* Total Active Services */}
        <div
          className={`rounded-[12px] transition-shadow ${hoveredCard === "total" ? "shadow-lg" : "shadow-sm"}`}
          onClick={() => handleCardClick("total", "")}
          onMouseEnter={() => setHoveredCard("total")}
          onMouseLeave={() => setHoveredCard(null)}
          style={getCardStyle("total")}
        >
          <DashboardFleetSummaryCard
            title="Total Active Services"
            value={metrics.totalActiveServices}
            changeText={`${Math.abs(metrics.monthlyGrowth)}% from previous month`}
            changeDirection={metrics.monthlyGrowth >= 0 ? "up" : "down"}
          />
        </div>

        {/* Most Requested Service */}
        {/* <div
          className={`rounded-[12px] transition-shadow ${hoveredCard === "most" ? "shadow-lg" : "shadow-sm"}`}
          onClick={() => handleCardClick("most", metrics.mostRequestedService.name)}
          onMouseEnter={() => setHoveredCard("most")}
          onMouseLeave={() => setHoveredCard(null)}
          style={getCardStyle("most")}
        >
          <DashboardFleetSummaryCard
            title=""
            //title="Most Requested Service"
            //value={metrics.mostRequestedService.name}
            value={"Most Requested Service"}
            changeText={`${metrics.mostRequestedService.count} ${
              metrics.mostRequestedService.count === 1 ? "Order" : "Orders"
            }`}
            hideChangeIndicator={true}
          /> */}
        {/* </div> */}

        {/* Less Requested Service */}
        {/* <div
          className={`rounded-[12px] transition-shadow ${hoveredCard === "less" ? "shadow-lg" : "shadow-sm"}`}
          onClick={() => handleCardClick("less", metrics.lessRequestedService.name)}
          onMouseEnter={() => setHoveredCard("less")}
          onMouseLeave={() => setHoveredCard(null)}
          style={getCardStyle("less")}
        >
          <DashboardFleetSummaryCard
            title=""
            //title="Less Requested Service"
            //value={metrics.lessRequestedService.name}
            value={"Less Requested Service"}
            changeText={`${metrics.lessRequestedService.count} ${
              metrics.lessRequestedService.count === 1 ? "Order" : "Orders"
            }`}
            hideChangeIndicator={true}
          /> */}
        {/* </div> */}

      </div>
    </section>
  );
};

export default ServicesSummary;